// The help beside the Steps, Subforms and Preview tabs of the workflow editor.
//
// WHY THIS EXISTS
//
// The editor is three bare forms and a preview, and nothing on any of them
// says what the others are for: a fresh workflow's Steps tab is an empty list
// with an Add button, and "subform" is a word the person designing their first
// workflow has not met. pagews carries the same explanation beside its Values,
// Fields and Subforms tabs (components/types-help there), keyed by the screen
// because where a value is READ differs per screen. Here there is one screen
// and one reader — the person filling the workflow in through its share link —
// so the words are three fixed paragraphs rather than a table of contexts.
//
// The vocabulary is the editor's own: a STEP is one screen of the form, a
// FIELD is one question on it, a SUBFORM is a named group of fields any
// `object` or `list` field can ask for, and the VALUES are the answers, saved
// as a response when the last step is submitted (docs/wfws.md).
import React from "react";
import Help from "@repoflow/components/help";

export const StepsHelp = () => (
  <Help label="What are steps and fields?">
    <p className="mb-2">
      A workflow is an ordered list of steps, and each step is one screen of
      the form a person fills in through the share link — its label is the
      heading they see. A step's fields are the questions on that screen. Give
      a field a label (its name, which the answer is stored under, fills in
      from the label), a type, and whatever that type needs: the choices of an
      options field, the bounds of a number, whether a date also stores a time.
      The Details column sums those up for each field.
    </p>
    <p className="mb-0">
      Text, options, number, checkbox, colour, date, time and icon each hold
      one answer. A required field has to be answered before the person can
      move on; a hidden one is declared but not asked — a hidden text field
      is filled in from the link instead, so a page that embeds the form can
      say which page it is (see the Share tab). List and object hold
      structured content and point at a subform, declared on the Subforms tab.
    </p>
  </Help>
);

export const SubformsHelp = () => (
  <Help label="What are subforms?">
    <p className="mb-2">
      A subform is a named group of fields — an address, a contact, a line
      item — declared once for the whole workflow. On its own it appears
      nowhere: go to a step, add a field of type <code>object</code> (for one)
      or <code>list</code> (for many), and point it at the subform by name. The
      person filling in that step then sees the subform's fields inside it,
      once per row.
    </p>
    <p className="mb-0">
      A subform can be asked for by any number of fields on any number of
      steps, and by fields of other subforms.
    </p>
  </Help>
);

export const ValuesHelp = () => (
  <Help label="What are values?">
    <p className="mb-2">
      Values are the answers a person gives: one per field, stored under the
      field's name, step by step, and saved as a response when the last step
      is submitted. Each response is listed on the workflow's Responses tab,
      keyed by step and field name.
    </p>
    <p className="mb-0">
      The preview below is those forms, compiled from the steps as they are
      being designed. Type into it freely — nothing entered here is kept.
    </p>
  </Help>
);
